import type { ReactNode, ButtonHTMLAttributes } from 'react'

/**
 * The shared design system. Both roles use these components — the member
 * interface is the same pieces, larger and fewer.
 *
 * Sizing is in rem throughout so that iOS text-size settings scale the whole
 * interface. Do not use px for anything a parent has to read.
 */

type ButtonSize = 'normal' | 'large'
type ButtonTone = 'primary' | 'secondary' | 'quiet' | 'danger'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode
  size?: ButtonSize
  tone?: ButtonTone
}

const TONES: Record<ButtonTone, string> = {
  primary: 'bg-ink text-canvas active:bg-ink/85',
  secondary: 'bg-surface text-ink border border-line active:bg-canvas',
  quiet: 'bg-transparent text-muted active:text-ink',
  danger: 'bg-danger text-white active:bg-danger/85',
}

export function Button({
  children,
  size = 'normal',
  tone = 'primary',
  className = '',
  ...rest
}: ButtonProps) {
  // 'large' is the member-facing size. The 4.5rem minimum is deliberate:
  // it stays comfortably tappable even at the largest accessibility text size.
  const sizing =
    size === 'large'
      ? 'min-h-[4.5rem] text-xl px-6 rounded-2xl font-medium'
      : 'min-h-[3rem] text-base px-4 rounded-xl font-medium'

  return (
    <button
      {...rest}
      className={`w-full flex items-center justify-center gap-3 transition-colors disabled:opacity-40 ${sizing} ${TONES[tone]} ${className}`}
    >
      {children}
    </button>
  )
}

export function Card({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div className={`bg-surface border border-line rounded-2xl p-4 ${className}`}>
      {children}
    </div>
  )
}

/**
 * Section headers hide themselves when they have nothing to show.
 * Empty sections are a product rule, not a styling preference: no "0 items"
 * rows anywhere in this app.
 */
export function Section({
  title,
  count,
  children,
}: {
  title: string
  count?: number
  children: ReactNode
}) {
  if (count === 0) return null
  return (
    <section className="mb-6">
      <h2 className="text-xs uppercase tracking-wide text-muted mb-2 px-1">
        {title}
        {count !== undefined && count > 0 ? ` (${count})` : ''}
      </h2>
      {children}
    </section>
  )
}

/**
 * The scrolling body of a screen.
 *
 * It does NOT add the top safe-area inset, and that is the whole point. Almost
 * every Screen in the app sits under a header that has already pushed itself
 * clear of the notch; adding the inset again put a second notch-height of
 * empty space between the header and the first card — about 47px of nothing at
 * the top of every list on a modern iPhone. The payments tab had already
 * noticed and quietly stopped using Screen to avoid it, which left the same
 * gap in eight other places.
 *
 * The bottom inset stays here, because the bottom of the scrolling area really
 * is the bottom of the screen.
 *
 * A screen that owns the top edge itself — sign-in, onboarding, anything with
 * no header above it — adds `pad-safe-top` through className. Explicit, and
 * countable: `grep pad-safe-top` now lists exactly the things that touch the
 * top of the display.
 */
export function Screen({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div className={`flex-1 scroll-area pt-1 pad-safe-bottom px-4 ${className}`}>
      {children}
    </div>
  )
}

/** A labelled input. Large by default — parents use these too. */
export function Field({
  label,
  hint,
  ...rest
}: {
  label: string
  hint?: string
} & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="block mb-4">
      <span className="block text-sm text-muted mb-1.5">{label}</span>
      <input
        {...rest}
        className="w-full min-h-[3.25rem] px-4 rounded-xl bg-surface border border-line text-lg
                   focus:outline-none focus:border-ink transition-colors"
      />
      {hint && <span className="block text-xs text-muted mt-1.5">{hint}</span>}
    </label>
  )
}

export function ErrorNote({ children }: { children: ReactNode }) {
  if (!children) return null
  return (
    <p className="text-danger text-sm mb-4" role="alert">
      {children}
    </p>
  )
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex-1 grid place-items-center text-muted text-sm">
      <div className="text-center">
        <div className="w-6 h-6 mx-auto mb-3 rounded-full border-2 border-line border-t-ink animate-spin" />
        {label}
      </div>
    </div>
  )
}

export interface TabItem {
  id: string
  label: string
  icon: ReactNode
}

/**
 * The admin tab bar. Sits above the home indicator via safe-area padding, or
 * it will be half-unreachable on any iPhone with a notch.
 */
export function TabBar({
  tabs,
  active,
  onSelect,
}: {
  tabs: TabItem[]
  active: string
  onSelect: (id: string) => void
}) {
  return (
    <nav className="shrink-0 border-t border-line bg-surface pad-safe-bottom">
      <div className="flex">
        {tabs.map((tab) => {
          const isActive = tab.id === active
          return (
            <button
              key={tab.id}
              onClick={() => onSelect(tab.id)}
              aria-current={isActive ? 'page' : undefined}
              className={`flex-1 flex flex-col items-center gap-1 py-2 min-h-[3.25rem] transition-colors ${
                isActive ? 'text-ink' : 'text-muted'
              }`}
            >
              <span aria-hidden>{tab.icon}</span>
              <span className="text-[0.6875rem] leading-none">{tab.label}</span>
            </button>
          )
        })}
      </div>
    </nav>
  )
}

/**
 * A segmented control — the iOS one, in the app's own clothes.
 *
 * Used for views of the same data (Upcoming / Calendar / Paid), never for
 * navigation between different things. That is the whole distinction between
 * a segment and a tab, and mixing them is how an app stops making sense.
 */
export function Segmented({
  options,
  active,
  onSelect,
  className = '',
}: {
  options: { id: string; label: string }[]
  active: string
  onSelect: (id: string) => void
  className?: string
}) {
  return (
    <div className={`flex gap-1 p-1 rounded-xl bg-line/50 ${className}`} role="tablist">
      {options.map((option) => {
        const isActive = option.id === active
        return (
          <button
            key={option.id}
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(option.id)}
            // `min-w-0` and `truncate` are the whole point. Without them a flex
            // child refuses to shrink below its text, so a fourth segment does
            // not squeeze the others — it pushes the row off the side of the
            // screen and takes the last tabs with it. That is how the calendar
            // disappeared the day Agenda was added.
            className={`flex-1 min-w-0 truncate px-1 min-h-[2.5rem] rounded-lg text-sm font-medium transition-colors ${
              isActive ? 'bg-surface text-ink shadow-sm' : 'text-muted'
            }`}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
